import { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import ListingSectionWithMetadata from '../transports/listing-section-with-metadata-transport'

interface FetchSectionPageResult {
    sections: ListingSectionWithMetadata[]
    totalItems: number
}

interface SectionPaginationResult {
    allSections: ListingSectionWithMetadata[]
    totalItems: number | null
    loading: boolean
    error: Error | null
    hasNextPage: boolean
    currentPage: number
    nextPage: () => Promise<void>
    reset: () => void
}

function useLazySectionedPagination(
    fetchPage: (page: number) => Promise<FetchSectionPageResult>,
    pageSize: number,
    autoFetch = true,
): SectionPaginationResult {
    const [currentPage, setCurrentPage] = useState(0)
    const [allSections, setAllSections] = useState<ListingSectionWithMetadata[]>([])
    const [totalItems, setTotalItems] = useState<number | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<Error | null>(null)
    const hasInitialized = useRef(false)
    const requestedPagesRef = useRef<Set<number>>(new Set())
    const versionRef = useRef(0)

    const loadingRef = useRef(false)
    const currentPageRef = useRef(0)
    const totalItemsRef = useRef<number | null>(null)

    const totalPages = totalItems === null ? null : Math.ceil(totalItems / pageSize)
    const hasNextPage = totalPages === null || currentPage < totalPages

    const nextPage = useCallback(async () => {
        const knownTotal = totalItemsRef.current
        const reachedEnd = knownTotal !== null && currentPageRef.current >= Math.ceil(knownTotal / pageSize)

        if (loadingRef.current || reachedEnd) {
            return
        }

        const pageToFetch = currentPageRef.current + 1

        if (requestedPagesRef.current.has(pageToFetch)) {
            return
        }

        const capturedVersion = versionRef.current

        requestedPagesRef.current.add(pageToFetch)
        loadingRef.current = true
        setLoading(true)
        setError(null)

        try {
            const result = await fetchPage(pageToFetch)
            if (versionRef.current !== capturedVersion) {
                return
            }
            currentPageRef.current = pageToFetch
            totalItemsRef.current = result.totalItems
            setAllSections(prev => [...prev, ...result.sections])
            setTotalItems(result.totalItems)
            setCurrentPage(pageToFetch)
        } catch (error_) {
            if (versionRef.current !== capturedVersion) {
                return
            }
            // Release the page so it can be retried
            requestedPagesRef.current.delete(pageToFetch)
            setError(error_ instanceof Error ? error_ : new Error('Failed to fetch page'))
        } finally {
            if (versionRef.current === capturedVersion) {
                loadingRef.current = false
                setLoading(false)
            }
        }
    }, [fetchPage, pageSize])

    const reset = useCallback(() => {
        versionRef.current += 1
        loadingRef.current = false
        currentPageRef.current = 0
        totalItemsRef.current = null
        setCurrentPage(0)
        setAllSections([])
        setTotalItems(null)
        setLoading(false)
        setError(null)
        requestedPagesRef.current.clear()
        hasInitialized.current = false
    }, [])

    useEffect(() => {
        if (autoFetch && !hasInitialized.current && currentPage === 0 && allSections.length === 0) {
            hasInitialized.current = true
            nextPage()
        }
    }, [autoFetch, currentPage, allSections.length, nextPage])

    return useMemo(
        () => ({
            allSections,
            totalItems,
            loading,
            error,
            hasNextPage,
            currentPage,
            nextPage,
            reset,
        }),
        [allSections, totalItems, loading, error, hasNextPage, currentPage, nextPage, reset],
    )
}

export default useLazySectionedPagination
